// components/PostCard.tsx
// Reusable card component for displaying posts in the content library

import React from 'react';
import { BarkBoardPost } from '../types';
import './PostCard.css';

interface PostCardProps {
    post: BarkBoardPost;
    onDelete: (postId: string) => void;
    onEdit: (post: BarkBoardPost) => void;
    isDeleting: boolean;
}

export const PostCard: React.FC<PostCardProps> = ({ post, onDelete, onEdit, isDeleting }) => {
    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const getTypeIcon = () => {
        switch (post.type) {
            case 'video': return '🎥';
            case 'audio': return '🎵';
            case 'article': return '📝';
            default: return '📄';
        }
    };

    const getTypeBadgeClass = () => {
        switch (post.type) {
            case 'video': return 'type-video';
            case 'audio': return 'type-audio';
            case 'article': return 'type-article';
            default: return 'type-default';
        }
    };

    const getThumbnail = () => {
        if (post.thumbnailUrl) {
            return post.thumbnailUrl;
        }
        // Fallback placeholder based on type
        switch (post.type) {
            case 'video': return 'https://via.placeholder.com/300x200/667eea/ffffff?text=Video';
            case 'audio': return 'https://via.placeholder.com/300x200/764ba2/ffffff?text=Audio';
            case 'article': return 'https://via.placeholder.com/300x200/f093fb/ffffff?text=Article';
            default: return 'https://via.placeholder.com/300x200/cccccc/ffffff?text=Post';
        }
    };

    return (
        <div className="post-card">
            <div className="post-card-thumbnail">
                <img src={getThumbnail()} alt={post.title} />
                <div className={`post-type-badge ${getTypeBadgeClass()}`}>
                    {getTypeIcon()} {post.type}
                </div>
            </div>

            <div className="post-card-content">
                <h3 className="post-title">{post.title}</h3>
                <p className="post-caption">
                    {post.caption.length > 120
                        ? post.caption.substring(0, 120) + '...'
                        : post.caption
                    }
                </p>

                <div className="post-meta">
                    <div className="meta-item">
                        <span className="meta-icon">📅</span>
                        <span className="meta-text">{formatDate(post.publishedAt || post.createdAt)}</span>
                    </div>
                    {post.category && (
                        <div className="meta-item">
                            <span className="meta-icon">🏷️</span>
                            <span className="meta-text">{post.category}</span>
                        </div>
                    )}
                </div>

                <div className="post-stats">
                    <div className="stat">
                        <span className="stat-icon">👁️</span>
                        <span className="stat-value">{post.viewCount || 0}</span>
                    </div>
                    <div className="stat">
                        <span className="stat-icon">👏</span>
                        <span className="stat-value">{post.complimentCount || 0}</span>
                    </div>
                </div>
            </div>

            <div className="post-card-actions">
                <button
                    onClick={() => onEdit(post)}
                    className="action-button edit-button"
                    disabled={isDeleting}
                >
                    ✏️ Edit
                </button>
                <button
                    onClick={() => onDelete(post.id)}
                    className="action-button delete-button"
                    disabled={isDeleting}
                >
                    {isDeleting ? '⏳ Deleting...' : '🗑️ Delete'}
                </button>
            </div>
        </div>
    );
};